import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ConfirmContext = createContext(null);

/**
 * Provider globale per dialog di conferma.
 * Espone `confirm(title, description?, opts?)` che ritorna Promise<boolean>.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Eliminare?', 'Operazione irreversibile.', { destructive: true });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Conferma',
    cancelLabel: 'Annulla',
    destructive: false,
    digita: '',
  });
  const [scritto, setScritto] = useState('');
  const resolverRef = useRef(null);

  const confirm = useCallback((title, description = '', {
    confirmLabel = 'Conferma',
    cancelLabel = 'Annulla',
    destructive = false,
    digita = '',   // azioni massive: per confermare va scritto questo testo (es. il nome)
  } = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setScritto('');
      setState({ open: true, title, description, confirmLabel, cancelLabel, destructive, digita });
    });
  }, []);

  const handleConfirm = () => {
    setState(s => ({ ...s, open: false }));
    resolverRef.current?.(true);
  };

  const handleCancel = () => {
    setState(s => ({ ...s, open: false }));
    resolverRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={open => { if (!open) handleCancel(); }}>
        {/* Stile alert iOS: centrato, angoli ampi, bottoni a tutta larghezza su telefono. */}
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-2xl p-5 sm:rounded-2xl">
          <AlertDialogHeader className="text-center sm:text-center">
            <AlertDialogTitle className="text-[17px] font-semibold">{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription className="whitespace-pre-line text-[13px] text-gray-600">{state.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {state.digita && (
            <div className="space-y-1.5">
              <p className="text-center text-[13px] text-gray-600">
                Per confermare scrivi <strong className="font-semibold text-gray-900">{state.digita.trim()}</strong>
              </p>
              <input
                autoFocus
                value={scritto}
                onChange={e => setScritto(e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#D70015]"
                aria-label={`Scrivi ${state.digita} per confermare`}
              />
            </div>
          )}
          <AlertDialogFooter className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:space-x-0">
            <AlertDialogCancel onClick={handleCancel} className="mt-0 w-full rounded-xl">{state.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={!!state.digita && scritto.trim() !== state.digita.trim()}
              className={`w-full rounded-xl ${state.destructive ? 'bg-[#D70015] hover:bg-red-700 focus:ring-[#D70015]' : ''}`}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
